import * as React from "react";

import { AutocompleteInputExpects } from "../../../Common/form/ui2023/autocomplete-input";

export interface DataMutableSourceShippingSessionStorageDataConsumer {
  lastFormattedAutocompletedAddress_forShipping?: AutocompleteInputExpects;
  lastFormattedAutocompletedAddress_forBilling?: AutocompleteInputExpects;
}
const defaultValue =
  {} as unknown as DataMutableSourceShippingSessionStorageDataConsumer;
const DataMutableSourceShippingSessionStorageDataContext =
  React.createContext(defaultValue);

export interface DataMutableSourceShippingSessionStorageData_Controller {
  handleLastFormattedAutocompletedAddressChange_forShipping: (
    lastFormattedAutocompletedAddress: AutocompleteInputExpects
  ) => Promise<void>;
  handleLastFormattedAutocompletedAddressChange_forBilling: (
    lastFormattedAutocompletedAddress: AutocompleteInputExpects
  ) => Promise<void>;
}
const controllerDefaultValue =
  {} as unknown as DataMutableSourceShippingSessionStorageData_Controller;
const DataMutableSourceShippingSessionStorageData_ControllerContext =
  React.createContext(controllerDefaultValue);

enum DataMutableSourceShippingSessionStorageDataKey {
  LastFormattedAutocompletedAddress_forShipping = "LastFormattedAutocompletedAddress_forShipping",
  LastFormattedAutocompletedAddress_forBilling = "LastFormattedAutocompletedAddress_forBilling",
}

export const DataMutableSourceShippingSessionStorageDataProvider: React.FunctionComponent =
  (props) => {
    const [
      lastFormattedAutocompletedAddress_forShipping,
      setLastFormattedAutocompletedAddress_forShipping,
    ] = React.useState(() => {
      try {
        const lastFormattedAutocompletedAddress_storedItem =
          sessionStorage.getItem(
            DataMutableSourceShippingSessionStorageDataKey.LastFormattedAutocompletedAddress_forShipping
          );
        if (lastFormattedAutocompletedAddress_storedItem) {
          return JSON.parse(lastFormattedAutocompletedAddress_storedItem);
        }
      } catch (anyError) {
        return null;
      }
    });
    const [
      lastFormattedAutocompletedAddress_forBilling,
      setLastFormattedAutocompletedAddress_forBilling,
    ] = React.useState(() => {
      try {
        const lastFormattedAutocompletedAddress_storedItem =
          sessionStorage.getItem(
            DataMutableSourceShippingSessionStorageDataKey.LastFormattedAutocompletedAddress_forBilling
          );
        if (lastFormattedAutocompletedAddress_storedItem) {
          return JSON.parse(lastFormattedAutocompletedAddress_storedItem);
        }
      } catch (anyError) {
        return null;
      }
    });

    const handleLastFormattedAutocompletedAddressChange_forShipping: DataMutableSourceShippingSessionStorageData_Controller["handleLastFormattedAutocompletedAddressChange_forShipping"] =
      React.useCallback(async (lastFormattedAutocompletedAddress) => {
        sessionStorage.setItem(
          DataMutableSourceShippingSessionStorageDataKey.LastFormattedAutocompletedAddress_forShipping,
          JSON.stringify(lastFormattedAutocompletedAddress)
        );

        setLastFormattedAutocompletedAddress_forShipping(
          lastFormattedAutocompletedAddress
        );
      }, []);

    const handleLastFormattedAutocompletedAddressChange_forBilling: DataMutableSourceShippingSessionStorageData_Controller["handleLastFormattedAutocompletedAddressChange_forBilling"] =
      React.useCallback(async (lastFormattedAutocompletedAddress) => {
        sessionStorage.setItem(
          DataMutableSourceShippingSessionStorageDataKey.LastFormattedAutocompletedAddress_forBilling,
          JSON.stringify(lastFormattedAutocompletedAddress)
        );

        setLastFormattedAutocompletedAddress_forBilling(
          lastFormattedAutocompletedAddress
        );
      }, []);

    const value: DataMutableSourceShippingSessionStorageDataConsumer =
      React.useMemo(
        () => ({
          lastFormattedAutocompletedAddress_forShipping,
          lastFormattedAutocompletedAddress_forBilling,
        }),
        [
          lastFormattedAutocompletedAddress_forShipping,
          lastFormattedAutocompletedAddress_forBilling,
        ]
      );
    const controllerValue: DataMutableSourceShippingSessionStorageData_Controller =
      React.useMemo(
        () => ({
          handleLastFormattedAutocompletedAddressChange_forShipping,
          handleLastFormattedAutocompletedAddressChange_forBilling,
        }),
        [
          handleLastFormattedAutocompletedAddressChange_forShipping,
          handleLastFormattedAutocompletedAddressChange_forBilling,
        ]
      );
    return (
      <DataMutableSourceShippingSessionStorageDataContext.Provider
        value={value}
      >
        <DataMutableSourceShippingSessionStorageData_ControllerContext.Provider
          value={controllerValue}
        >
          {props?.children || []}
        </DataMutableSourceShippingSessionStorageData_ControllerContext.Provider>
      </DataMutableSourceShippingSessionStorageDataContext.Provider>
    );
  };

export const useDataMutableSourceShippingSessionStorageData = () => {
  const context = React.useContext(
    DataMutableSourceShippingSessionStorageDataContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutableSourceShippingSessionStorageData must be used within a DataMutableSourceShippingSessionStorageDataProvider"
    );
  }
  return context;
};

export const useDataMutableSourceShippingSessionStorageData_asController =
  () => {
    const context = React.useContext(
      DataMutableSourceShippingSessionStorageData_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutableSourceShippingSessionStorageData_asController must be used within a DataMutableSourceShippingSessionStorageDataProvider"
      );
    }
    return context;
  };
